import type { TaskNode } from "./types";
import type { NodeMetrics } from "./tree"; // type-only: tree.ts imports nothing back → no cycle
import { currentPath } from "./paths";

export interface TagColor {
  solidBg: string;
  solidFg: string;
  softBg: string;
  softFg: string;
  border: string;
}

// every distinct tag in use (own tags + link targets), sorted — for autocomplete
export function allTags(tree: TaskNode[]): string[] {
  const set = new Set<string>();
  (function walk(list: TaskNode[]) {
    list.forEach((n) => {
      if (n.tag) set.add(n.tag);
      (n.links || []).forEach((t) => set.add(t));
      walk(n.children);
    });
  })(tree);
  return [...set].sort((a, b) => a.localeCompare(b));
}

// deterministic color per tag name so groups read at a glance
export function tagColor(tag: string): TagColor {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) % 360;
  return {
    solidBg: `hsl(${h} 60% 42%)`,
    solidFg: "#fff",
    softBg: `hsl(${h} 70% 96%)`,
    softFg: `hsl(${h} 65% 32%)`,
    border: `hsl(${h} 55% 75%)`,
  };
}

// ids to highlight for the active tag: every matching task PLUS its whole subtree
export function tagMatchSet(tree: TaskNode[], activeTag: string | null): Set<string> {
  const set = new Set<string>();
  if (!activeTag) return set;
  (function walk(list: TaskNode[], ancestorMatch: boolean) {
    list.forEach((n) => {
      const self = n.tag === activeTag || (n.links || []).includes(activeTag);
      const on = ancestorMatch || self;
      if (on) set.add(n.id);
      walk(n.children, on);
    });
  })(tree, false);
  return set;
}

// tasks that LINK to this tag (their links[] include it), as full paths
export function tagUsagePaths(tree: TaskNode[], tag: string): string[] {
  const out: string[] = [];
  (function walk(list: TaskNode[]) {
    list.forEach((n) => {
      if ((n.links || []).includes(tag)) out.push(currentPath(tree, n.id));
      walk(n.children);
    });
  })(tree);
  return out;
}

/* ---------- dependency estimates (via tags) ---------- */
// A node's own tag marks it as a "feature" (its subtree = that feature's work); a node's
// links[] are the features its subtree depends on. The dependency "extra" of a task is the
// rolled-up estimate of the feature(s) it depends on — surfaced next to the task's own
// estimate WITHOUT being added to it. Everything here is display-only (no rollup changes).

// REMAINING (not-done) estimate summed to the OUTERMOST node(s) that own each tag (nested/
// duplicate owners of the same tag don't double-count; distinct owners of one tag sum). Uses
// `remaining` so completed tasks — and fully-done features — drop out of the +DEP extra.
function ownerRollupByTag(tree: TaskNode[], metrics: Map<string, NodeMetrics>): Map<string, number> {
  const byTag = new Map<string, number>();
  (function walk(list: TaskNode[], ownedAbove: Set<string>) {
    list.forEach((n) => {
      const owns = !!n.tag && !ownedAbove.has(n.tag);
      if (owns) byTag.set(n.tag, (byTag.get(n.tag) || 0) + (metrics.get(n.id)?.remaining || 0));
      walk(n.children, owns ? new Set(ownedAbove).add(n.tag) : ownedAbove);
    });
  })(tree, new Set());
  return byTag;
}

// per-node subtree aggregates: the union of link targets and of owned tags across the
// node's whole subtree (the node itself + all descendants).
interface SubtreeTags { links: Set<string>; owned: Set<string> }
function subtreeTags(node: TaskNode, into: Map<string, SubtreeTags>): SubtreeTags {
  const links = new Set<string>(node.links || []);
  const owned = new Set<string>(node.tag ? [node.tag] : []);
  for (const c of node.children) {
    const cc = subtreeTags(c, into);
    cc.links.forEach((t) => links.add(t));
    cc.owned.forEach((t) => owned.add(t));
  }
  const agg = { links, owned };
  into.set(node.id, agg);
  return agg;
}

// feature dependency graph: outermost owner of tag X → the owned tags linked anywhere in X's
// subtree (i.e. the features X itself depends on). Used to walk transitive dependency chains.
function featureDepEdges(tree: TaskNode[], aggById: Map<string, SubtreeTags>, ownedTags: Set<string>): Map<string, Set<string>> {
  const edges = new Map<string, Set<string>>();
  (function walk(list: TaskNode[], ownedAbove: Set<string>) {
    list.forEach((n) => {
      const owns = !!n.tag && !ownedAbove.has(n.tag);
      if (owns) {
        const set = edges.get(n.tag) || new Set<string>();
        aggById.get(n.id)!.links.forEach((y) => { if (ownedTags.has(y) && y !== n.tag) set.add(y); });
        edges.set(n.tag, set);
      }
      walk(n.children, owns ? new Set(ownedAbove).add(n.tag) : ownedAbove);
    });
  })(tree, new Set());
  return edges;
}

// id -> dependency-extra estimate-days (only nodes with a positive extra are present).
// extra(N) = Σ remaining-rollup over EVERY feature reachable transitively from the tags linked
// in N's subtree (A → B → C counts B and C), deduped, cycle-safe, and excluding features owned
// inside N's own subtree (already its own work — but traversed through so their external deps
// still count). Done tasks/features drop out via the `remaining` metric in ownerRollupByTag.
export function dependencyExtras(tree: TaskNode[], metrics: Map<string, NodeMetrics>): Map<string, number> {
  const byTag = ownerRollupByTag(tree, metrics);
  const ownedTags = new Set(byTag.keys());
  const aggById = new Map<string, SubtreeTags>();
  tree.forEach((n) => subtreeTags(n, aggById));
  const featureDeps = featureDepEdges(tree, aggById, ownedTags);
  const out = new Map<string, number>();
  for (const [id, agg] of aggById) {
    const reached = new Set<string>();
    const stack = [...agg.links].filter((t) => ownedTags.has(t)); // direct deps (owned features)
    while (stack.length) {
      const t = stack.pop()!;
      if (reached.has(t)) continue;
      reached.add(t);
      for (const d of featureDeps.get(t) || []) if (!reached.has(d)) stack.push(d); // follow the chain
    }
    let sum = 0;
    reached.forEach((t) => { if (!agg.owned.has(t)) sum += byTag.get(t) || 0; });
    if (sum > 0) out.set(id, sum);
  }
  return out;
}

// A run of active leaves that move together in the Gantt: a "feature" group (nearest tagged
// ancestor/self) or an independent single leaf. `owns` = the group's feature tag (if any),
// `deps` = tags it depends on (ancestor links), `order` = display position for stable sorting.
interface LeafGroup { id: string; leaves: TaskNode[]; owns: string; deps: Set<string>; order: number }

// Active leaves in dependency-aware order for the Gantt: each feature is laid out, then
// immediately the tasks that depend on it, then the next feature, then independents — so a
// prerequisite always precedes its dependents AND sits right next to them. Cycles fall back
// to display order (and are surfaced separately by dependencyCycles).
//   docOrder=false (default): "feature-grouped" — a feature's cluster appears at the feature's
//     display position (seeded from features/independents first).
//   docOrder=true: "document order" — walk top-to-bottom; when a blocked task is reached its
//     prerequisite is pulled in on demand, so the cluster appears at the dependent's position.
export function dependencyLeafOrder(tree: TaskNode[], docOrder = false): TaskNode[] {
  // 1) group the active leaves by their nearest tagged ancestor (or themselves), in display order
  const groups: LeafGroup[] = [];
  const byId = new Map<string, LeafGroup>();
  let seq = 0;
  (function walk(list: TaskNode[], featureId: string, featureTag: string, ancLinks: string[]) {
    for (const n of list) {
      if (n.disabled) continue;
      const links = n.tag || n.links.length ? ancLinks.concat(n.links) : ancLinks;
      const fId = n.tag ? n.id : featureId;      // nearest tagged ancestor/self defines the group
      const fTag = n.tag ? n.tag : featureTag;
      if (n.children.length === 0) {
        const gid = fId || n.id;                 // untagged, no tagged ancestor → own singleton group
        let g = byId.get(gid);
        if (!g) { g = { id: gid, leaves: [], owns: fId ? fTag : "", deps: new Set(), order: seq++ }; byId.set(gid, g); groups.push(g); }
        g.leaves.push(n);
        links.forEach((t) => g!.deps.add(t));
      } else walk(n.children, fId, fTag, links);
    }
  })(tree, "", "", []);

  // 2) feature graph: which groups own / depend on each tag; a group is a "dependent" only if
  //    it depends on a tag some group actually owns (dangling links → treated as independent)
  const ownerGroupsByTag = new Map<string, LeafGroup[]>();
  for (const g of groups) if (g.owns) (ownerGroupsByTag.get(g.owns) ?? ownerGroupsByTag.set(g.owns, []).get(g.owns)!).push(g);
  const dependentGroupsByTag = new Map<string, LeafGroup[]>();
  for (const g of groups) for (const t of g.deps) if (ownerGroupsByTag.has(t)) (dependentGroupsByTag.get(t) ?? dependentGroupsByTag.set(t, []).get(t)!).push(g);
  const isDependent = (g: LeafGroup) => [...g.deps].some((t) => ownerGroupsByTag.has(t));

  // 3) DFS: feature first (prerequisites), then its dependents right after; seed from
  //    non-dependents in display order so sibling dependents keep their order
  const out: TaskNode[] = [];
  const done = new Set<string>(), active = new Set<string>();
  function dfs(g: LeafGroup) {
    if (done.has(g.id) || active.has(g.id)) return; // active-guard breaks dependency cycles
    active.add(g.id);
    for (const t of g.deps) for (const o of ownerGroupsByTag.get(t) ?? []) dfs(o); // prerequisites first
    done.add(g.id); out.push(...g.leaves); active.delete(g.id);
    if (g.owns) for (const d of dependentGroupsByTag.get(g.owns) ?? []) dfs(d);     // dependents right after
  }
  if (!docOrder) for (const g of groups) if (!isDependent(g)) dfs(g); // feature-grouped: features first
  for (const g of groups) dfs(g); // document order (single pass) OR catch dependents orphaned by cycles
  return out;
}

// directed cycles in the feature dependency graph (edge X→Y when a node inside feature X's
// owner-subtree links tag Y, and Y is itself owned somewhere). Each cycle is returned as an
// ordered tag list closing on itself, e.g. ["A","B","A"]. Dangling links (unowned target)
// aren't edges, so they can't form a cycle.
export function dependencyCycles(tree: TaskNode[]): string[][] {
  // edges per owned tag: union of links inside each owner-subtree, restricted to owned tags
  const aggById = new Map<string, SubtreeTags>();
  tree.forEach((n) => subtreeTags(n, aggById));
  const owned = new Set<string>();
  (function collect(list: TaskNode[]) { list.forEach((n) => { if (n.tag) owned.add(n.tag); collect(n.children); }); })(tree);

  const edges = new Map<string, Set<string>>();
  (function walk(list: TaskNode[], ownedAbove: Set<string>) {
    list.forEach((n) => {
      const owns = !!n.tag && !ownedAbove.has(n.tag);
      if (owns) {
        const set = edges.get(n.tag) || new Set<string>();
        aggById.get(n.id)!.links.forEach((y) => { if (owned.has(y) && y !== n.tag) set.add(y); });
        // a feature linking its OWN tag is a self-cycle
        if ((n.links || []).includes(n.tag) || aggById.get(n.id)!.links.has(n.tag)) set.add(n.tag);
        edges.set(n.tag, set);
      }
      walk(n.children, owns ? new Set(ownedAbove).add(n.tag) : ownedAbove);
    });
  })(tree, new Set());

  const cycles: string[][] = [];
  const seen = new Set<string>();       // normalized cycle keys already recorded
  const color = new Map<string, number>(); // 0 white, 1 grey (on stack), 2 black
  const stack: string[] = [];
  function record(startTag: string) {
    const i = stack.indexOf(startTag);
    const cyc = stack.slice(i).concat(startTag); // close the loop
    const rotated = cyc.slice(0, -1);
    const key = [...rotated].sort().join("|") + "#" + rotated.length; // rotation-invariant-ish key
    if (!seen.has(key)) { seen.add(key); cycles.push(cyc); }
  }
  function dfs(tag: string) {
    color.set(tag, 1); stack.push(tag);
    for (const y of edges.get(tag) || []) {
      const c = color.get(y) || 0;
      if (c === 1) record(y);           // back-edge → cycle
      else if (c === 0) dfs(y);
    }
    stack.pop(); color.set(tag, 2);
  }
  for (const tag of owned) if ((color.get(tag) || 0) === 0) dfs(tag);
  return cycles;
}

// A dependency is only deliverable if its feature ships no later than the task that needs it.
// Flag every case where a task LINKS a tag whose feature completes in a LATER version than the
// task's own version (both must have a version from the defined `versions` list to compare).
export interface VersionConflict {
  tag: string;               // the depended-on feature tag
  dependentPath: string;     // the task that depends on it (breadcrumb)
  dependentVersion: string;  // the task's version (earlier)
  featureVersion: string;    // the feature's completion version (later → the conflict)
}
export function dependencyVersionConflicts(tree: TaskNode[], versions: string[]): VersionConflict[] {
  const rank = (v: string) => versions.indexOf(v); // -1 when empty / not in the defined list
  // feature completion version per owned tag = the LATEST version among the feature's leaves
  // (nearest tagged ancestor attributes each leaf to its feature)
  const featRank = new Map<string, number>();
  const featVer = new Map<string, string>();
  (function walk(list: TaskNode[], ownerTag: string) {
    for (const n of list) {
      if (n.disabled) continue;
      const tag = n.tag || ownerTag;
      if (n.children.length === 0) {
        if (tag) { const r = rank(n.version); if (r >= 0 && r > (featRank.get(tag) ?? -1)) { featRank.set(tag, r); featVer.set(tag, n.version); } }
      } else walk(n.children, tag);
    }
  })(tree, "");
  const out: VersionConflict[] = [];
  (function walk(list: TaskNode[]) {
    for (const n of list) {
      if (n.disabled) continue;
      const dr = rank(n.version);
      if (dr >= 0) for (const t of n.links) {
        const fr = featRank.get(t);
        if (fr != null && fr > dr) {
          out.push({ tag: t, dependentPath: currentPath(tree, n.id), dependentVersion: n.version, featureVersion: featVer.get(t)! });
        }
      }
      walk(n.children);
    }
  })(tree);
  return out;
}

// rename a tag everywhere it appears (own tags + links); merges onto an existing
// tag and de-dupes links. Returns true if anything could change (valid, non-no-op).
export function renameTag(tree: TaskNode[], oldTag: string, newTag: string): boolean {
  newTag = (newTag || "").trim();
  if (!newTag || newTag === oldTag) return false; // no-op
  (function walk(list: TaskNode[]) {
    list.forEach((n) => {
      if (n.tag === oldTag) n.tag = newTag;
      if (Array.isArray(n.links)) n.links = [...new Set(n.links.map((t) => (t === oldTag ? newTag : t)))];
      walk(n.children);
    });
  })(tree);
  return true;
}
